<template>
    <UModal
        v-if="(isDesktop || alwaysModal) && !alwaysDrawer"
        v-model:open="open"
        :dismissible="dismissible"
        :title="title"
        :ui="mergedModalUi"
    >
        <template #header>
            <slot name="title" v-bind="{ title, close }">
                <div :class="chrome.shellRoot">
                    <UButton
                        icon="lucide:x"
                        variant="ghost"
                        color="neutral"
                        :ui="closeButtonUi"
                        :class="chrome.closeButton"
                        aria-label="Close"
                        @click="close()"
                    >
                        <template #leading>
                            <SharedAppIcon
                                name="lucide:x"
                                :size="20"
                                :stroke-width="2.5"
                                class="text-inherit transition-opacity duration-200 group-active:opacity-50"
                            />
                        </template>
                    </UButton>
                    <div :class="chrome.shellHead">
                        <SharedEyebrow v-if="eyebrow" :ui="{ base: chrome.eyebrow }">
                            {{ eyebrow }}
                        </SharedEyebrow>
                        <h2 :class="chrome.heading">
                            <template v-if="titleLead || titleEmphasis">
                                {{ titleLead }}<em v-if="titleEmphasis">{{ titleEmphasis }}</em>
                            </template>
                            <template v-else>{{ title }}</template>
                        </h2>
                        <p v-if="sub" :class="chrome.sub">{{ sub }}</p>
                    </div>
                    <div v-if="showGoldRule" :class="chrome.goldRule" aria-hidden="true" />
                </div>
            </slot>
            <!-- Pinned under title (e.g. picker search) — does not scroll with body. -->
            <slot name="header" v-bind="{ open, close }" />
        </template>
        <template #body>
            <slot name="body" v-bind="{ open, close }">
                <div class="px-2">
                    <p v-if="description">{{ description }}</p>
                </div>
            </slot>
        </template>
        <template #footer>
            <slot name="footer" v-bind="{ open, close }">
                <div :class="chrome.footerRow">
                    <CraneButton
                        :brand="noButtonBrand"
                        :class="footerNoButtonClass"
                        :label="noButtonLabel"
                        @click="close(false as T)"
                    />
                    <div class="grow-1"></div>
                    <CraneButton
                        :brand="yesButtonBrand"
                        :class="footerYesButtonClass"
                        :label="yesButtonLabel"
                        @click="close(true as T)"
                    />
                </div>
            </slot>
        </template>
    </UModal>

    <UDrawer
        v-else
        v-model:open="open"
        :title="title"
        :dismissible="dismissible"
        :handle="false"
        :handle-only="!dismissible"
        :ui="mergedDrawerUi"
    >
        <template #title>
            <slot name="title" v-bind="{ title, close }">
                <div :class="chrome.shellRoot">
                    <UButton
                        icon="lucide:x"
                        variant="ghost"
                        color="neutral"
                        :ui="closeButtonUi"
                        :class="chrome.closeButton"
                        aria-label="Close"
                        @click="close()"
                    >
                        <template #leading>
                            <SharedAppIcon
                                name="lucide:x"
                                :size="20"
                                :stroke-width="2.5"
                                class="text-inherit transition-opacity duration-200 group-active:opacity-50"
                            />
                        </template>
                    </UButton>
                    <div :class="chrome.shellHead">
                        <SharedEyebrow v-if="eyebrow" :ui="{ base: chrome.eyebrow }">
                            {{ eyebrow }}
                        </SharedEyebrow>
                        <h2 :class="chrome.heading">
                            <template v-if="titleLead || titleEmphasis">
                                {{ titleLead }}<em v-if="titleEmphasis">{{ titleEmphasis }}</em>
                            </template>
                            <template v-else>{{ title }}</template>
                        </h2>
                        <p v-if="sub" :class="chrome.sub">{{ sub }}</p>
                    </div>
                    <div v-if="showGoldRule" :class="chrome.goldRule" aria-hidden="true" />
                </div>
            </slot>
            <div v-if="$slots.header" class="shrink-0 mt-2.5" data-vaul-no-drag>
                <slot name="header" v-bind="{ open, close }" />
            </div>
        </template>

        <template #body>
            <div class="flex h-full min-h-0 flex-col overflow-hidden">
                <div class="min-h-0 flex-1 overflow-y-auto" data-vaul-no-drag>
                    <slot name="body" v-bind="{ open, close }">
                        <div class="px-2">
                            <p v-if="description">{{ description }}</p>
                        </div>
                    </slot>
                </div>
                <div v-if="$slots.footer" class="shrink-0" data-vaul-no-drag>
                    <slot name="footer" v-bind="{ open, close }">
                        <div :class="chrome.footerRow">
                            <CraneButton
                                :brand="noButtonBrand"
                                :class="footerNoButtonClass"
                                :label="noButtonLabel"
                                @click="close(false as T)"
                            />
                            <div class="grow-1"></div>
                            <CraneButton
                                :brand="yesButtonBrand"
                                :class="footerYesButtonClass"
                                :label="yesButtonLabel"
                                @click="close(true as T)"
                            />
                        </div>
                    </slot>
                </div>
            </div>
        </template>
    </UDrawer>
</template>

<script setup lang="ts" generic="T = unknown">
/**
 * Adaptive drawer: `UModal` when `(isDesktop || alwaysModal) && !alwaysDrawer`,
 * otherwise `UDrawer` bottom sheet. Designer currently forces `isDesktop === false`,
 * so most call sites use the sheet unless `alwaysModal` is set.
 *
 * `dismissible` enables overlay/outside/ESC close; sheet drag uses Vaul (`handle-only`
 * when not dismissible). Mark scroll regions with `data-vaul-no-drag` so iOS body
 * scroll does not move the sheet. Right-click drag is blocked on `[data-vaul-drawer]`.
 *
 * Default chrome matches Handwriting App v2 `.hm-drawer`: cream sheet, Playfair title,
 * gold eyebrow, topnav close, optional gold dash (`show-rule`, default on).
 * Pass `eyebrow` / `titleLead` / `titleEmphasis` / `sub`, or plain `title`.
 * Override `#title` for non-standard chrome (printing process, paper picker, Avalara).
 * `#header` stays pinned under the title (picker search — MVP `.hm-picker-pinned`);
 * `#body` scrolls.
 */

import CraneButton from '../shared/CraneButton.vue'
import SharedAppIcon from '../shared/AppIcon.vue'
import SharedEyebrow from '../shared/Eyebrow.vue'
import useTopnavButtonUi from '@/composables/useTopnavButton'
import { CAPACITOR_DRAWER_BELOW_TOPNAV_CONTENT_CLASS } from '@/utils/capacitorTopnav'
import { mergeUiClassMap } from '@/utils/defuStrConcat'

/** Partial `UModal` slot classes merged over AdaptiveDrawer modal defaults. */
export type AdaptiveDrawerModalUi = Partial<{
    overlay: string
    content: string
    body: string
    header: string
    footer: string
}>

/** Partial `UDrawer` slot classes merged over AdaptiveDrawer drawer defaults. */
export type AdaptiveDrawerDrawerUi = Partial<{
    overlay: string
    content: string
    container: string
    title: string
    body: string
    handle: string
}>

/**
 * Optional `ui` overrides (shared-wrapper pattern): defaults live here; callers pass
 * a partial map. Drawer `content` is merged again after Capacitor height so chrome
 * overrides cannot drop the topnav sheet height.
 */
/** Footer brand recipes — same set ConfirmDialog uses for cancel / confirm. */
export type AdaptiveDrawerFooterBrand =
    | 'navy-outline'
    | 'navy-solid'
    | 'error-solid'
    | 'error-outline'
    | 'green-on-light'

export type AdaptiveDrawerUi = {
    modal?: AdaptiveDrawerModalUi
    drawer?: AdaptiveDrawerDrawerUi
    /** Override Playfair heading classes (merged over the standard title). */
    heading?: string
    noButton?: { base?: string; color?: string; brand?: AdaptiveDrawerFooterBrand }
    yesButton?: { base?: string; color?: string; brand?: AdaptiveDrawerFooterBrand }
}

const props = withDefaults(
    defineProps<{
        /** Title for the drawer/modal (aria + Playfair heading when lead/emphasis unset). */
        title: string
        /** Gold eyebrow above the Playfair title. */
        eyebrow?: string
        /** Playfair title lead, e.g. "Choose ". Pair with `titleEmphasis`. */
        titleLead?: string
        /** Italic Playfair emphasis, e.g. "Address". */
        titleEmphasis?: string
        /** Supporting line under the title. */
        sub?: string
        /**
         * Gold dash under the header. Default on; pass `show-rule="false"` for search pickers.
         */
        showRule?: boolean
        /** Description for the drawer/modal, overridden by the `body` slot */
        description?: string
        /** Partial UI overrides merged over modal/drawer/chrome defaults via `mergeUiClassMap`. */
        ui?: AdaptiveDrawerUi
        /** Label for the "No" button in the default footer slot */
        noButtonLabel?: string
        /** Label for the "Yes" button in the default footer slot */
        yesButtonLabel?: string
        drawerId?: string
        alwaysDrawer?: boolean
        alwaysModal?: boolean
        /**
         * When true: overlay, outside tap, and ESC can close. When false (default):
         * close via X (and footer actions); sheet drag-to-dismiss is limited via `handle-only`.
         */
        dismissible?: boolean
    }>(),
    {
        dismissible: false,
        eyebrow: '',
        titleLead: '',
        titleEmphasis: '',
        sub: '',
    }
)

const runtimeConfig = useRuntimeConfig() as { public?: { NUXT_CAPACITOR_APP?: boolean } }
const isCapacitorApp = computed(() => Boolean(runtimeConfig.public?.NUXT_CAPACITOR_APP))

const { isDesktop } = useIsDesktop()

/** HM chrome dash under the title. Off only when `show-rule="false"` (search pickers). */
const showGoldRule = computed(() => props.showRule !== false)

const closeButtonUi = useTopnavButtonUi(computed(() => 'light' as const))

/**
 * Map legacy `ui.*.color` CSS vars onto CraneButton brands so delete confirms
 * that still pass `--color-error-700` keep a red solid without Nuxt outline paint.
 */
function brandFromLegacyColor(
    color: string | undefined,
    fallback: AdaptiveDrawerFooterBrand
): AdaptiveDrawerFooterBrand {
    if (!color) return fallback
    if (color.includes('error')) return 'error-solid'
    return fallback
}

const noButtonBrand = computed(
    (): AdaptiveDrawerFooterBrand =>
        props.ui?.noButton?.brand ?? brandFromLegacyColor(props.ui?.noButton?.color, 'navy-outline')
)

const yesButtonBrand = computed(
    (): AdaptiveDrawerFooterBrand =>
        props.ui?.yesButton?.brand ?? brandFromLegacyColor(props.ui?.yesButton?.color, 'navy-solid')
)

/**
 * UI map: desktop vs mobile header/footer chrome in one place.
 * One standard Playfair title; override size via `ui.heading` if needed.
 * Nuxt UI modal/drawer slot classes stay in `mergedModalUi` / `mergedDrawerUi`.
 */
const chrome = computed(() => {
    const desktop = isDesktop.value

    const defaults = {
        shellRoot: 'relative w-full',
        // Title/header top inset: drawer `#title` uses `pt-8` (MVP `.hm-drawer-head` 32px).
        // Keep shellHead free of extra pt-* / eyebrow mt-* (was double-stacking on sheet).
        shellHead: desktop ? 'flex flex-col gap-2 pr-16' : 'flex flex-col gap-2.75 pr-10',
        eyebrow: 'm-0',
        heading: [
            'my-0 font-serif text-5xl font-normal leading-[1.1] tracking-tight',
            'text-(--primary-700) [&_em]:italic',
        ].join(' '),
        sub: 'mt-1 mb-0 max-w-[38ch] font-sans text-md font-normal leading-(--leading-body) text-(--neutral-700)',
        closeButton: 'absolute top-0 -right-1 z-2',
        // MVP `.hm-drawer-head::after`: 28×1.5px (`w-7`, `h-[0.09375rem]`); horizontal align via `#title` `px-5`.
        goldRule: 'mt-3.5 mb-2 h-[0.09375rem] w-7 shrink-0 bg-(--gold)',
        footerRow: 'flex w-full grow-1 items-center gap-2.5',
        // ConfirmDialog-sized DS footer actions (border/font from CraneButton brand)
        footerButton: 'h-11 cursor-pointer justify-center px-4 font-sans text-md font-medium',
    }

    if (!props.ui?.heading) return defaults
    return {
        ...defaults,
        heading: mergeUiClassMap({ heading: defaults.heading }, { heading: props.ui.heading })
            .heading as string,
    }
})

const footerNoButtonClass = computed(() =>
    [chrome.value.footerButton, props.ui?.noButton?.base].filter(Boolean).join(' ')
)

const footerYesButtonClass = computed(() =>
    [chrome.value.footerButton, props.ui?.yesButton?.base].filter(Boolean).join(' ')
)

/**
 * Default `UModal` slot classes — cream sheet, no header hairline.
 * Merged with `props.ui.modal` via `mergeUiClassMap` (caller wins conflicts).
 */
const ADAPTIVE_MODAL_UI_DEFAULTS = {
    overlay: 'bg-(--ui-scrim)',
    content:
        'max-w-[800px] max-h-[80dvh] min-w-[50vw] flex flex-col overflow-hidden rounded bg-(--neutral-50) ring-0',
    body: 'flex-1 min-h-0 p-0 flex flex-col overflow-y-auto bg-(--neutral-50)',
    header: 'relative flex flex-col items-stretch justify-start border-none min-h-0 px-6 sm:px-8 py-4 bg-(--neutral-50)',
    footer: 'border-none bg-(--neutral-50) px-6 sm:px-8 pb-6 pt-0',
} as const satisfies Record<string, string>

/**
 * Default `UDrawer` slot classes — cream sheet + 18px top radius (MVP `.hm-drawer-sheet`).
 * Capacitor sheet height: `--capacitor-drawer-below-topnav-height` in main.css
 * (overrides Nuxt UI `mt-24` / `max-h-[96%]`).
 */
const ADAPTIVE_DRAWER_UI_DEFAULTS = {
    content:
        'adaptive-drawer-content mx-auto rounded-t-[1.125rem] bg-(--neutral-50) overflow-hidden ring-0',
    container: 'h-full gap-0 !p-0 bg-(--neutral-50)',
    title: 'relative sticky top-0 z-10 block w-full bg-(--neutral-50) px-5 pt-8 pb-2.75 font-normal',
    body: 'p-0 overflow-y-auto bg-(--neutral-50)',
} as const satisfies Record<string, string>

const mergedModalUi = computed(() =>
    mergeUiClassMap(
        ADAPTIVE_MODAL_UI_DEFAULTS as Record<string, unknown>,
        (props.ui?.modal ?? {}) as Record<string, unknown>
    )
)

const mergedDrawerUi = computed(() => {
    // Caller `ui.drawer`, then Capacitor height last so content classes cannot
    // replace the topnav sheet height via object-spread key overwrite.
    const withCaller = mergeUiClassMap(
        ADAPTIVE_DRAWER_UI_DEFAULTS as Record<string, unknown>,
        (props.ui?.drawer ?? {}) as Record<string, unknown>
    )
    if (!isCapacitorApp.value) {
        return withCaller
    }
    return mergeUiClassMap(withCaller, {
        content: CAPACITOR_DRAWER_BELOW_TOPNAV_CONTENT_CLASS,
    })
})

const emit = defineEmits<{
    close: [result: T | undefined]
}>()

/** Controls whether the drawer/modal is open or not */
const open = defineModel<boolean>('open', { default: false })

// Intercept back button/swipe back to close drawer/modal instead of navigating
useDrawerHistory(open)

/**
 * Vaul starts drag on any pointerdown + setPointerCapture. Right-click (button 2)
 * never gets a clean release, so the sheet follows the mouse — block non-primary.
 */
function onNonPrimaryPointerDownCapture(event: PointerEvent) {
    if (event.button === 0) return
    const target = event.target
    if (!(target instanceof Element)) return
    if (!target.closest('[data-vaul-drawer]')) return
    event.stopImmediatePropagation()
}

onMounted(() => {
    document.addEventListener('pointerdown', onNonPrimaryPointerDownCapture, true)
})

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onNonPrimaryPointerDownCapture, true)
})

/** Temporarily holds the last result to emit when `open` changes */
const lastResult = ref<T>()

// When `open` is set to `false`, emit the `lastResult` and then clear it
watch(open, (newOpen) => {
    if (newOpen) return
    emit('close', lastResult.value)
    lastResult.value = undefined
})

/**
 * Closes the drawer/dialog with a result, if one was given
 */
const close = (result?: T) => {
    lastResult.value = result
    open.value = false
}
</script>

<style>
.adaptive-drawer-content {
    max-width: var(--ui-container-mobile);
}
</style>
